import { z } from 'zod';

/**
 * Fractional indexing for ordering animations within the pin.
 * This allows for concurrent drag-and-drop reordering in the editor UI.
 * The order is scoped to the entire pin, not just the pin component that this
 * animation is part of.
 */
export const AnimationOrderSchema = z.string();

/** An animation for a component. */
export const BasicAnimationSchema = z.object({
  type: z.literal('basic'),
  order: AnimationOrderSchema,
});

/**
 * Set of animations for a component.
 * Currently, we just have an entry animation.
 */
export const BasicAnimationSetSchema = z.object({
  entry: BasicAnimationSchema,
});

/** An entry animation for a text component. */
export const TextAnimationSchema = z.object({
  type: z.literal('text'),
  order: AnimationOrderSchema,

  /**
   * Controls how the text animation is applied.
   * - 'component': Animate the entire text component as one unit (default)
   * - 'paragraph': Animate each paragraph separately (bullets and sub-bullets count as
   *   separate paragraphs too)
   *
   * Defaults to 'component' if not specified.
   */
  granularity: z.enum(['component', 'paragraph']).prefault('component'),
});

/**
 * Set of animations for a text component.
 *
 * Currently, we just have an entry animation.
 */
export const TextAnimationSetSchema = z.object({
  /** The entry animation for this text component. */
  entry: TextAnimationSchema,
});

export type AnimationOrder = z.infer<typeof AnimationOrderSchema>;
export type BasicAnimation = z.infer<typeof BasicAnimationSchema>;
export type BasicAnimationSet = z.infer<typeof BasicAnimationSetSchema>;
export type TextAnimation = z.infer<typeof TextAnimationSchema>;
export type TextAnimationSet = z.infer<typeof TextAnimationSetSchema>;
